import React from "react"
import { Alert, Table } from "react-bootstrap"
import { useSelector } from "react-redux"

import {
  getProductSummary,
  getSelectedProduct,
} from "@/onboarding/features/cost_analysis/selectors"
import { formatMoney } from "@/helpers/money"
import ErrorBoundary from "@/components/ErrorBoundary"
import { InfoCircle } from "@/components/Icons"

const SummaryDataRow = ({ label, summary, currency, showFinancials }): JSX.Element => {
  const targetMet = summary.currentMargin >= summary.marginTarget

  return (
    <tr>
      <td>{label}</td>
      <td className="text-right">{summary.expectedAdults}</td>
      <td className="text-right">{summary.expectedChildren}</td>
      <td className="text-right">{summary.expectedGuests}</td>
      {showFinancials && (
        <>
          <td className="text-right">{formatMoney(summary.salesTarget, currency)}</td>
          <td className="text-right">{formatMoney(summary.currentCosts, currency)}</td>
          <td className="text-right">{formatMoney(summary.marginTarget, currency)}</td>
          <td className={targetMet ? "text-success text-right" : "text-danger text-right"}>
            {formatMoney(summary.currentMargin, currency)}
          </td>
        </>
      )}
    </tr>
  )
}

const SummaryTable = (): JSX.Element => {
  const summary = useSelector(getProductSummary)
  const { netCurrencyCode, rackCurrencyCode } = useSelector(getSelectedProduct)

  // Sales targets are in rack/sell currency and costs are in net/buy currency.
  // When they differ, the margin calculation is meaningless without FX, so
  // hide the financial columns and explain why.
  const crossCurrency = netCurrencyCode !== rackCurrencyCode

  return (
    <div>
      <div className="d-flex align-items-center">
        <h2 className="h5">Target Performance Summary</h2>
        <InfoCircle
          className="ms-2 mt-n2 cursor-pointer"
          data-toggle="tooltip"
          data-placement="top"
          title="Calculates the expected revenue, costs, and margins using target pax, selected operator pricing, and anticipated retail rates submitted during the prebidding setup."
        />
      </div>
      {crossCurrency && (
        <Alert variant="info" className="mb-2">
          Sales, costs and margins are unavailable because Tour Cost Currency ({netCurrencyCode})
          and Sell Rate Currency ({rackCurrencyCode}) differ.
        </Alert>
      )}
      <ErrorBoundary>
        <Table>
          <thead>
            <tr>
              <th scope="col" />
              <th scope="col" className="text-right">
                Adults
              </th>
              <th scope="col" className="text-right">
                Children
              </th>
              <th scope="col" className="text-right">
                Total Guests
              </th>
              {!crossCurrency && (
                <>
                  <th scope="col" className="text-right">
                    Sales Target
                  </th>
                  <th scope="col" className="text-right">
                    Projected Costs
                  </th>
                  <th scope="col" className="text-right">
                    Margin Target
                  </th>
                  <th scope="col" className="text-right">
                    Projected Margin
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            <SummaryDataRow
              label="Per Call"
              summary={summary.daily}
              currency={netCurrencyCode}
              showFinancials={!crossCurrency}
            />
            <SummaryDataRow
              label="Per Season"
              summary={summary.season}
              currency={netCurrencyCode}
              showFinancials={!crossCurrency}
            />
          </tbody>
        </Table>
      </ErrorBoundary>
    </div>
  )
}

export default SummaryTable
